import type { Line, PlacedItem } from './types';

export interface ElectricalSpecificationWarning {
  code: string;
  lineId?: string;
  message: string;
}

export interface ElectricalSpecification {
  powerPoints: {
    total: number;
    byType: Record<string, number>;
    excludedByType: Record<string, number>;
  };
  weakCurrent: {
    total: number;
    byType: Record<string, number>;
  };
  frames: {
    total: number;
    byPosts: Record<number, number>;
  };
  boxes: {
    brickConcrete: {
      totalMechanisms: number;
    };
    drywall: {
      byPosts: Record<number, number>;
    };
  };
  switches: {
    byLine: Record<string, {
      mechanisms: number;
      interruptores: number;
      conmutadores: number;
      cruzamientos: number;
    }>;
    totals: {
      interruptores: number;
      conmutadores: number;
      cruzamientos: number;
    };
  };
  warnings: ElectricalSpecificationWarning[];
}

interface SwitchMechanism {
  lineId: string;
  sourceType: 'switch' | 'switch_double';
}

const SOCKET_MECHANISM_COUNTS: Record<string, number> = {
  socket_single: 1,
  socket_double: 2,
  socket_triple: 3,
  socket_quadruple: 4,
  socket_3ph: 1,
  bathroom_socket: 1,
  kitchen_socket: 1,
};

const APPLIANCE_POWER_POINT_COUNTS: Record<string, number> = {
  washing_machine: 1,
  microwave: 1,
  extractor_hood: 1,
  refrigerator: 1,
  freezer: 1,
  boiler: 1,
  dryer: 1,
  electric_heater: 1,
  hydromassage_bath: 1,
  dishwasher: 1,
};

const EXCLUDED_POWER_POINT_TYPES = new Set([
  'air_conditioner',
  'oven',
  'hob',
  'ev_charger',
]);

const WEAK_CURRENT_MECHANISM_COUNTS: Record<string, number> = {
  tv_socket: 1,
  rj45_socket: 1,
};

const STANDALONE_FRAME_POST_COUNTS: Record<string, number> = {
  ...SOCKET_MECHANISM_COUNTS,
  ...APPLIANCE_POWER_POINT_COUNTS,
  switch: 1,
  switch_double: 1,
  // Weak-current outlets: one physical mechanism each (frame post / box),
  // counted like switches but not as power points. Standalone gets its own
  // frame; sharing a blockId folds into that block's frame.
  tv_socket: 1,
  rj45_socket: 1,
};

export function getPowerPointMechanismCount(type: string): number {
  return SOCKET_MECHANISM_COUNTS[type] ?? APPLIANCE_POWER_POINT_COUNTS[type] ?? 0;
}

export function getWeakCurrentCount(type: string): number {
  return WEAK_CURRENT_MECHANISM_COUNTS[type] ?? 0;
}

export function getFramePostCount(type: string): number {
  return STANDALONE_FRAME_POST_COUNTS[type] ?? 0;
}

export function isExcludedPowerPointType(type: string): boolean {
  return EXCLUDED_POWER_POINT_TYPES.has(type);
}

function addCount(target: Record<string, number>, key: string, amount = 1): void {
  target[key] = (target[key] ?? 0) + amount;
}

function addPostCount(target: Record<number, number>, posts: number, amount = 1): void {
  if (posts <= 0) return;
  target[posts] = (target[posts] ?? 0) + amount;
}

function switchLineIds(item: PlacedItem): SwitchMechanism[] {
  if (item.type === 'switch') {
    const lineId = item.lineId?.trim();
    return lineId ? [{ lineId, sourceType: 'switch' }] : [];
  }

  if (item.type !== 'switch_double') return [];

  return [item.switchLines?.left, item.switchLines?.right].flatMap(lineId => {
    const normalized = lineId?.trim();
    return normalized ? [{ lineId: normalized, sourceType: 'switch_double' as const }] : [];
  });
}

export function buildElectricalSpecification(
  items: PlacedItem[],
  lines: Line[] = [],
): ElectricalSpecification {
  void lines;

  const powerByType: Record<string, number> = {};
  const excludedByType: Record<string, number> = {};
  const weakByType: Record<string, number> = {};
  const frameByPosts: Record<number, number> = {};
  const drywallByPosts: Record<number, number> = {};
  const switchMechanismsByLine = new Map<string, SwitchMechanism[]>();
  const blockMechanismCounts = new Map<string, number>();

  let powerTotal = 0;
  let weakTotal = 0;
  let brickConcreteTotal = 0;

  for (const item of items) {
    const powerCount = getPowerPointMechanismCount(item.type);
    const weakCount = getWeakCurrentCount(item.type);
    if (powerCount > 0) {
      powerTotal += powerCount;
      addCount(powerByType, item.type, powerCount);
    } else if (weakCount > 0) {
      weakTotal += weakCount;
      addCount(weakByType, item.type, weakCount);
    } else if (isExcludedPowerPointType(item.type)) {
      addCount(excludedByType, item.type);
    }

    const mechanismCount = getFramePostCount(item.type);
    if (mechanismCount > 0) {
      brickConcreteTotal += mechanismCount;
      const blockId = item.blockId?.trim();
      if (blockId) {
        blockMechanismCounts.set(blockId, (blockMechanismCounts.get(blockId) ?? 0) + mechanismCount);
      } else {
        addPostCount(frameByPosts, mechanismCount);
        addPostCount(drywallByPosts, mechanismCount);
      }
    }

    for (const mechanism of switchLineIds(item)) {
      switchMechanismsByLine.set(mechanism.lineId, [
        ...(switchMechanismsByLine.get(mechanism.lineId) ?? []),
        mechanism,
      ]);
    }
  }

  for (const mechanismCount of blockMechanismCounts.values()) {
    addPostCount(frameByPosts, mechanismCount);
    addPostCount(drywallByPosts, mechanismCount);
  }

  const switchByLine: ElectricalSpecification['switches']['byLine'] = {};
  const switchTotals = {
    interruptores: 0,
    conmutadores: 0,
    cruzamientos: 0,
  };
  const warnings: ElectricalSpecificationWarning[] = [];

  for (const [lineId, mechanisms] of switchMechanismsByLine.entries()) {
    const mechanismCount = mechanisms.length;
    const lineCounts = mechanismCount === 1
      ? { mechanisms: mechanismCount, interruptores: 1, conmutadores: 0, cruzamientos: 0 }
      : mechanismCount === 2
        ? { mechanisms: mechanismCount, interruptores: 0, conmutadores: 2, cruzamientos: 0 }
        : { mechanisms: mechanismCount, interruptores: 0, conmutadores: 2, cruzamientos: mechanismCount - 2 };

    switchByLine[lineId] = lineCounts;
    switchTotals.interruptores += lineCounts.interruptores;
    switchTotals.conmutadores += lineCounts.conmutadores;
    switchTotals.cruzamientos += lineCounts.cruzamientos;

    if (lineCounts.cruzamientos > 0 && mechanisms.slice(2).some(mechanism => mechanism.sourceType === 'switch_double')) {
      warnings.push({
        code: 'double_cruzamiento',
        lineId,
        message: `Line ${lineId}: double cruzamiento may be hard to source; verify availability.`,
      });
    }
  }

  return {
    powerPoints: {
      total: powerTotal,
      byType: powerByType,
      excludedByType,
    },
    weakCurrent: {
      total: weakTotal,
      byType: weakByType,
    },
    frames: {
      total: Object.values(frameByPosts).reduce((sum, count) => sum + count, 0),
      byPosts: frameByPosts,
    },
    boxes: {
      brickConcrete: {
        totalMechanisms: brickConcreteTotal,
      },
      drywall: {
        byPosts: drywallByPosts,
      },
    },
    switches: {
      byLine: switchByLine,
      totals: switchTotals,
    },
    warnings,
  };
}
